import { useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import type { OverviewStore } from '../api/overview'
import { formatCurrency, formatNumber } from '../lib/format'

/**
 * A stable, high-contrast palette indexed by store number (001 → 0, 022 → 21).
 * Keeping colours tied to the shop means a refresh cannot repaint the chart.
 */
const PALETTE = [
  '#0ea5e9', '#f97316', '#22c55e', '#a855f7', '#ef4444',
  '#14b8a6', '#eab308', '#6366f1', '#ec4899', '#84cc16',
  '#06b6d4', '#f59e0b', '#8b5cf6', '#10b981', '#f43f5e',
  '#3b82f6', '#d946ef', '#65a30d', '#0d9488', '#fb923c',
  '#4f46e5', '#be123c',
]

const SIZE = 220
const CENTER = SIZE / 2
const OUTER_RADIUS = 94
const INNER_RADIUS = 58

interface Slice {
  code: string
  name: string
  value: number
  share: number
  color: string
  startAngle: number
  endAngle: number
}

function colorFor(code: string): string {
  const number = Number.parseInt(code, 10)
  return PALETTE[(Number.isFinite(number) ? number - 1 : 0) % PALETTE.length]
}

function polar(radius: number, angle: number): { x: number; y: number } {
  const radians = ((angle - 90) * Math.PI) / 180
  return {
    x: CENTER + radius * Math.cos(radians),
    y: CENTER + radius * Math.sin(radians),
  }
}

function donutPath(startAngle: number, endAngle: number): string {
  const outerStart = polar(OUTER_RADIUS, startAngle)
  const outerEnd = polar(OUTER_RADIUS, endAngle)
  const innerEnd = polar(INNER_RADIUS, endAngle)
  const innerStart = polar(INNER_RADIUS, startAngle)
  const largeArc = endAngle - startAngle > 180 ? 1 : 0
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${OUTER_RADIUS} ${OUTER_RADIUS} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${INNER_RADIUS} ${INNER_RADIUS} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ')
}

interface SalesShareChartProps {
  stores: OverviewStore[]
  /** Number of shops requested, so the badge can say “17 of 19 shops”. */
  requested?: number
}

/**
 * Interactive sales-share donut for the General Overview.
 *
 * Every positive shop's period sales is one slice; the whole is the sum of
 * those parts. Hover/focus to preview a shop, click to pin it, click again to
 * return to the total. Shops without a feed or with zero/negative sales are
 * excluded — they have no share of the pie to draw.
 */
export function SalesShareChart({ stores, requested }: SalesShareChartProps) {
  const [hovered, setHovered] = useState<string | null>(null)
  const [pinned, setPinned] = useState<string | null>(null)

  const slices = useMemo<Slice[]>(() => {
    const positive = stores
      .filter((store) => (store.sales_period_eur ?? 0) > 0)
      .map((store) => ({
        code: store.store_code,
        name: store.store_name,
        value: store.sales_period_eur as number,
      }))
      .sort((a, b) => b.value - a.value)

    const total = positive.reduce((sum, store) => sum + store.value, 0)
    if (total <= 0) return []

    let cursor = 0
    return positive.map((store) => {
      const share = store.value / total
      const startAngle = cursor
      const endAngle = cursor + share * 360
      cursor = endAngle
      return {
        ...store,
        share,
        color: colorFor(store.code),
        startAngle,
        endAngle,
      }
    })
  }, [stores])

  const total = slices.reduce((sum, slice) => sum + slice.value, 0)
  const activeCode = hovered ?? pinned
  const active = slices.find((slice) => slice.code === activeCode) ?? null

  const togglePinned = (code: string) => {
    setPinned((current) => (current === code ? null : code))
  }

  const sliceEvents = (slice: Slice) => ({
    onMouseEnter: () => setHovered(slice.code),
    onMouseLeave: () => setHovered(null),
    onFocus: () => setHovered(slice.code),
    onBlur: () => setHovered(null),
    onClick: () => togglePinned(slice.code),
    onKeyDown: (event: ReactKeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        togglePinned(slice.code)
      }
    },
  })

  return (
    <section className="card space-y-4 p-4">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Sales by shop</h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Each slice is one shop’s sales for the period; the whole is their sum.
          </p>
        </div>
        <span className="badge bg-slate-500/10 text-slate-600 dark:bg-white/5 dark:text-slate-300">
          {requested && requested !== slices.length
            ? `${slices.length} of ${requested} shops`
            : `${slices.length} shop${slices.length === 1 ? '' : 's'}`}
        </span>
      </header>

      {slices.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-7">
          <div className="flex h-28 w-28 items-center justify-center rounded-full border-8 border-dashed border-slate-200 dark:border-white/10">
            <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500">
              No sales
            </span>
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            No shop in this selection has positive sales for the period.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[250px_minmax(0,1fr)] lg:items-center">
          <div className="mx-auto flex w-full max-w-[280px] flex-col items-center gap-1">
            <svg
              viewBox={`0 0 ${SIZE} ${SIZE}`}
              className="h-auto w-full max-w-[250px]"
              role="group"
              aria-label={`Sales by shop. ${slices.length} shops, total ${formatCurrency(total)}. Hover or focus a slice for detail; click to pin it.`}
            >
              {slices.length === 1 ? (
                <circle
                  cx={CENTER}
                  cy={CENTER}
                  r={(OUTER_RADIUS + INNER_RADIUS) / 2}
                  fill="none"
                  stroke={slices[0].color}
                  strokeWidth={OUTER_RADIUS - INNER_RADIUS}
                  className="cursor-pointer outline-none transition-opacity"
                  opacity={activeCode && activeCode !== slices[0].code ? 0.35 : 1}
                  role="button"
                  tabIndex={0}
                  aria-pressed={pinned === slices[0].code}
                  aria-label={`${slices[0].code} ${slices[0].name}: ${formatCurrency(
                    slices[0].value,
                  )} (100%)`}
                  {...sliceEvents(slices[0])}
                />
              ) : (
                slices.map((slice) => (
                  <path
                    key={slice.code}
                    d={donutPath(slice.startAngle, slice.endAngle)}
                    fill={slice.color}
                    stroke="rgba(255,255,255,0.7)"
                    strokeWidth={1}
                    className="cursor-pointer outline-none transition-opacity"
                    opacity={activeCode && activeCode !== slice.code ? 0.35 : 1}
                    role="button"
                    tabIndex={0}
                    aria-pressed={pinned === slice.code}
                    aria-label={`${slice.code} ${slice.name}: ${formatCurrency(
                      slice.value,
                    )} (${formatNumber(slice.share * 100, 1)}%)`}
                    {...sliceEvents(slice)}
                  />
                ))
              )}

              <text
                x={CENTER}
                y={active ? 96 : 100}
                textAnchor="middle"
                className="fill-slate-400 text-[9px] font-semibold uppercase tracking-wider dark:fill-slate-500"
              >
                {active ? `#${active.code}` : 'Period total'}
              </text>
              <text
                x={CENTER}
                y={active ? 116 : 120}
                textAnchor="middle"
                className="fill-slate-900 text-[15px] font-semibold dark:fill-white"
              >
                {formatCurrency(active ? active.value : total)}
              </text>
              <text
                x={CENTER}
                y={active ? 134 : 138}
                textAnchor="middle"
                className="fill-slate-500 text-[10px] dark:fill-slate-400"
              >
                {active
                  ? `${formatNumber(active.share * 100, 1)}%`
                  : `${slices.length} shop${slices.length === 1 ? '' : 's'}`}
              </text>
              {active && (
                <text
                  x={CENTER}
                  y={147}
                  textAnchor="middle"
                  className="fill-slate-500 text-[8px] dark:fill-slate-400"
                >
                  {active.name}
                </text>
              )}
            </svg>
            <p className="text-center text-[10px] text-slate-400 dark:text-slate-500">
              {pinned ? 'Click the slice again to show the total' : 'Hover a slice · click to pin'}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-0.5">
            {slices.map((slice) => {
              const isActive = activeCode === slice.code
              return (
                <button
                  key={slice.code}
                  type="button"
                  onClick={() => togglePinned(slice.code)}
                  onMouseEnter={() => setHovered(slice.code)}
                  onMouseLeave={() => setHovered(null)}
                  onFocus={() => setHovered(slice.code)}
                  onBlur={() => setHovered(null)}
                  aria-pressed={pinned === slice.code}
                  aria-label={`${slice.code} ${slice.name}: ${formatCurrency(
                    slice.value,
                  )} (${formatNumber(slice.share * 100, 1)}%)`}
                  className={`flex items-center gap-2 rounded-lg px-2 py-1 text-left text-[11px] transition-colors ${
                    isActive
                      ? 'bg-slate-100 dark:bg-white/10'
                      : 'hover:bg-slate-50 dark:hover:bg-white/5'
                  }`}
                >
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: slice.color }}
                    aria-hidden="true"
                  />
                  <span className="font-mono text-[9px] text-slate-400 dark:text-slate-500">
                    #{slice.code}
                  </span>
                  <span className="min-w-0 flex-1 break-words text-slate-600 dark:text-slate-300">
                    {slice.name}
                  </span>
                  <span className="tabular-nums text-slate-700 dark:text-slate-200">
                    {formatCurrency(slice.value)}
                  </span>
                  <span className="w-11 text-right tabular-nums text-slate-400 dark:text-slate-500">
                    {formatNumber(slice.share * 100, 1)}%
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </section>
  )
}

